import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSFlexBox,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IPayeeEngagementResponse,
  IPayeeResponse,
  PayerPayeeEngagementStatus
} from "@wingspanhq/payments/dist/interfaces";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useMutationUpdatePayeeEngagement } from "../../../query/payeeEngagements/mutations/useMutationUpdatePayeeEngagement";
import { FieldView } from "../../../shared/components/FieldView/FieldView";
import React from "react";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";

export interface RestoreEngagementModalProps {
  payee: IPayeeResponse;
  engagement: IPayeeEngagementResponse;
  onClose: () => void;
}

export const RestoreEngagementModal: React.FC<RestoreEngagementModalProps> = ({
  payee,
  engagement,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [updateEngagement, updateEngagementMeta] =
    useMutationUpdatePayeeEngagement();

  const payeeNames = wsName({
    user: payee.user!,
    member: payee.member
  });

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to restore this contractor engagement?
      </WSText.ParagraphSm>
      <WSPanel>
        <WSFlexBox gap="XL">
          <FieldView
            label="Engagement name"
            value={engagement.engagementName}
          />
          <FieldView
            label="Contractor name"
            value={payeeNames.getResolvedName()}
          />
        </WSFlexBox>
      </WSPanel>

      <WSAlert
        my="2XL"
        theme="info"
        icon="info-circle"
        size="M"
        title="Restoring a contractor engagement - what to expect:"
      >
        <WSElement as="ul" ml="L">
          <li>
            New invoices and payments can be created for this contractor
            engagement.
          </li>
          <li>
            Eligibility state for the contractor engagement will be restored
            (including any changes that have occurred for eligibility
            requirements).
          </li>
        </WSElement>
      </WSAlert>

      <WSErrorMessage
        error={updateEngagementMeta.error}
        mb="XL"
        contextKey="ArchiveClient"
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Restore contractor engagement",
            loading: updateEngagementMeta.isLoading,
            kind: "Primary",
            onAsyncClick: async () => {
              await updateEngagement(
                {
                  payeeId: payee.payeeId,
                  engagementId: engagement.payerPayeeEngagementId,
                  status: PayerPayeeEngagementStatus.Active
                },
                {
                  onSuccess: () => {
                    openSnackbar({
                      theme: "dark",
                      type: "success",
                      message: "Contractor engagement restored"
                    });
                    onClose();
                  }
                }
              );
            }
          }
        ]}
      />
    </>
  );
};

export const useRestoreEngagementModal = () => {
  return useWSModal(RestoreEngagementModal, {
    title: "Restore contractor engagement",
    size: "M"
  });
};
